import { useState, useCallback, useEffect } from "react"
import {
  PillTabs,
  PillTabsList,
  PillTabsTrigger,
  PillTabsContent,
} from "~/react/ui/pill-tabs"
import {
  PageHeader,
  PageHeaderTitle,
  PageHeaderActions,
} from "~/react/ui/page-header"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import EmailAccountsTable from "~/react/components/email_accounts_table"
import DomainsTable from "~/react/components/domains_table"
import EmailAccountsWidgets from "~/react/components/email-accounts-widgets"
import ConnectEmailAccountsSheet from "./connect_email_accounts_sheet"
import { campaigns_path } from "~/lib/routes"
import emptyStateImageUrl from "~/images/empty_states/data-verify.svg"

type TeamMember = {
  id: number
  name: string
}

type EmailAccountForDomains = {
  id: number
  email: string
}

type CreditCardInfo = {
  card_type: string
  last_4: string
  expiration_month: number | null
  expiration_year: string | null
}

type BillingInfo = {
  address: string | null
  city: string | null
  postal_code: string | null
  country_code: string | null
}

type Props = {
  teamMembers: TeamMember[]
  sharedWorkspace: boolean
  emailAccounts: EmailAccountForDomains[]
  totalDomainsCount: number
  isPremium: boolean
  isQuotaReached: boolean
  submenuHtml: string
  allowEmailProvisioning: boolean
  managedMailboxMonthlyPrice?: string
  currentEmailAccountsCount: number
  currentMaxEmailAccounts: number
  absoluteMaxEmailAccounts: number
  planIncludedEmailAccounts: number
  pricePerSlot: number
  currencySymbol: string
  currencyFormat: string
  billingCycle: string
  subscriptionLevel: number
  creditCardInfo?: CreditCardInfo | null
  nextBillingDate?: string | null
  billingPeriodStart?: string | null
  stripePublicKey?: string | null
  billingInfo?: BillingInfo | null
  userEmail?: string | null
  cardholderName?: string | null
  hasCreditCard?: boolean
  hasEmailAccounts: boolean
}

const getTabFromUrl = (): string => {
  if (typeof window === "undefined") return "email_accounts"
  const params = new URLSearchParams(window.location.search)
  return params.get("tab") || "email_accounts"
}

const getEmailAccountIdFromUrl = (): number | null => {
  if (typeof window === "undefined") return null
  const params = new URLSearchParams(window.location.search)
  const id = params.get("email_account_id")
  if (!id) return null
  if (id === "latest") return -1
  const parsed = parseInt(id, 10)
  return Number.isNaN(parsed) ? null : parsed
}

const getDomainIdFromUrl = (): string | null => {
  if (typeof window === "undefined") return null
  const params = new URLSearchParams(window.location.search)
  return params.get("domain_id")
}

const readUrlState = () => {
  const tab = getTabFromUrl()
  const emailAccountId = getEmailAccountIdFromUrl()
  const domainId = getDomainIdFromUrl()
  return { tab, emailAccountId, domainId }
}

const updateTabInUrl = (tab: string) => {
  if (typeof window === "undefined") return
  const params = new URLSearchParams(window.location.search)
  if (tab === "email_accounts") {
    params.delete("tab")
    // Keep URL canonical: email accounts tab shouldn't keep domain_id
    params.delete("domain_id")
  } else {
    params.set("tab", tab)
    // Keep URL canonical: domains tab shouldn't keep email_account_id
    params.delete("email_account_id")
  }
  const queryString = params.toString()
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname
  window.history.replaceState(null, "", newUrl)
}

const updateEmailAccountIdInUrl = (emailAccountId: number | null) => {
  if (typeof window === "undefined") return
  const params = new URLSearchParams(window.location.search)
  if (emailAccountId) {
    params.set("email_account_id", emailAccountId.toString())
    // Keep URL canonical
    params.delete("domain_id")
    // Switch to email_accounts tab if not already there
    if (params.get("tab") !== "email_accounts") {
      params.delete("tab")
    }
  } else {
    params.delete("email_account_id")
  }
  const queryString = params.toString()
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname
  window.history.replaceState(null, "", newUrl)
}

const updateDomainIdInUrl = (domainId: string | null) => {
  if (typeof window === "undefined") return
  const params = new URLSearchParams(window.location.search)
  if (domainId) {
    params.set("domain_id", domainId)
    // Keep URL canonical
    params.delete("email_account_id")
    // Switch to domains tab if not already there
    params.set("tab", "domains")
  } else {
    params.delete("domain_id")
  }
  const queryString = params.toString()
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname
  window.history.replaceState(null, "", newUrl)
}

export default function EmailAccountsCenter({
  teamMembers,
  sharedWorkspace,
  emailAccounts,
  totalDomainsCount,
  isPremium,
  isQuotaReached,
  submenuHtml,
  allowEmailProvisioning,
  managedMailboxMonthlyPrice = "$6",
  currentEmailAccountsCount,
  currentMaxEmailAccounts,
  absoluteMaxEmailAccounts,
  planIncludedEmailAccounts,
  pricePerSlot,
  currencySymbol,
  currencyFormat,
  billingCycle,
  subscriptionLevel,
  creditCardInfo,
  nextBillingDate,
  billingPeriodStart,
  stripePublicKey,
  billingInfo,
  userEmail,
  cardholderName,
  hasCreditCard,
  hasEmailAccounts,
}: Props) {
  const [activeTab, setActiveTab] = useState<string>(() => readUrlState().tab)
  const [selectedEmailAccountId, setSelectedEmailAccountId] = useState<
    number | null
  >(() => readUrlState().emailAccountId)
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(
    () => readUrlState().domainId,
  )
  const [sidepanelOpen, setSidepanelOpen] = useState(false)
  const [sidepanelFilter, setSidepanelFilter] = useState<
    "managed" | "connected" | undefined
  >(undefined)

  // Listen for URL changes (back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      const { tab, emailAccountId, domainId } = readUrlState()
      setActiveTab(tab)
      setSelectedEmailAccountId(emailAccountId)
      setSelectedDomainId(domainId)
    }
    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [])

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value)
    updateTabInUrl(value)
    if (value === "email_accounts") {
      setSelectedDomainId(null)
    } else if (value === "domains") {
      setSelectedEmailAccountId(null)
    }
  }, [])

  const handleOpenSidepanel = useCallback(
    (filter?: "managed" | "connected") => {
      setSidepanelFilter(filter)
      setSidepanelOpen(true)
    },
    [],
  )

  const handleEmailAccountIdChange = useCallback(
    (emailAccountId: number | null) => {
      updateEmailAccountIdInUrl(emailAccountId)
      setSelectedEmailAccountId(emailAccountId)
      // If an email account is opened, ensure domain is cleared in state too
      if (emailAccountId) {
        setSelectedDomainId(null)
        setActiveTab("email_accounts")
      }
    },
    [],
  )

  const handleDomainIdChange = useCallback((domainId: string | null) => {
    updateDomainIdInUrl(domainId)
    setSelectedDomainId(domainId)
    // If a domain is opened, ensure email account is cleared in state too
    if (domainId) {
      setSelectedEmailAccountId(null)
      setActiveTab("domains")
    }
  }, [])

  return (
    <>
      <div
        className="dashboard-main__submenu"
        dangerouslySetInnerHTML={{ __html: submenuHtml }}
      />
      <div className="dashboard-main__content">
        <div className="master-details">
          <div className="master-details__master">
            <div className="board-box pb-0">
              <PageHeader>
                <div className="page-header__heading">
                  <ol className="page-header-breadcrumb">
                    <li className="page-header-breadcrumb__item">
                      <a
                        className="page-header-breadcrumb__link"
                        href={campaigns_path()}
                      >
                        {i18n.t("js.backoffice.navigation.campaigns")}
                      </a>
                    </li>
                  </ol>

                  <PageHeaderTitle>
                    {i18n.t("js.email_accounts_center.title")}
                  </PageHeaderTitle>
                </div>

                <PageHeaderActions>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleOpenSidepanel("connected")}
                  >
                    <Icon icon="plus" size="sm" />
                    {i18n.t("js.email_accounts_center.add_email_account")}
                  </Button>
                </PageHeaderActions>
              </PageHeader>

              {hasEmailAccounts ? (
                <>
                  <EmailAccountsWidgets isPremium={isPremium} />

                  <PillTabs value={activeTab} onValueChange={handleTabChange}>
                    <PillTabsList className="mb-4">
                      <PillTabsTrigger value="email_accounts">
                        {i18n.t("js.email_accounts_g")}
                      </PillTabsTrigger>
                      <PillTabsTrigger value="domains">
                        {i18n.t("js.domains")}
                      </PillTabsTrigger>
                    </PillTabsList>

                    <PillTabsContent value="email_accounts">
                      <EmailAccountsTable
                        teamMembers={teamMembers}
                        sharedWorkspace={sharedWorkspace}
                        onOpenConnectSidepanel={handleOpenSidepanel}
                        allowEmailProvisioning={allowEmailProvisioning}
                        isPremium={isPremium}
                        initialEmailAccountId={selectedEmailAccountId}
                        onEmailAccountIdChange={handleEmailAccountIdChange}
                      />
                    </PillTabsContent>

                    <PillTabsContent value="domains">
                      <DomainsTable
                        total={totalDomainsCount}
                        emailAccounts={emailAccounts}
                        allowEmailProvisioning={allowEmailProvisioning}
                        isPremium={isPremium}
                        initialDomainId={selectedDomainId}
                        onDomainIdChange={handleDomainIdChange}
                      />
                    </PillTabsContent>
                  </PillTabs>
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-state__img">
                    <img src={emptyStateImageUrl} alt="" />
                  </div>
                  <div className="empty-state__content">
                    <h3 className="empty-state__title">
                      {i18n.t("js.email_accounts_center.empty_state.title")}
                    </h3>
                    <p
                      className="text-balance"
                      dangerouslySetInnerHTML={{
                        __html: i18n.t(
                          "js.email_accounts_center.empty_state.description_html",
                        ),
                      }}
                    />
                    <div className="empty-state__actions">
                      <Button size="sm" onClick={() => handleOpenSidepanel()}>
                        <Icon icon="plus" size="sm" />
                        {i18n.t("js.email_accounts_center.empty_state.cta")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConnectEmailAccountsSheet
        open={sidepanelOpen}
        onOpenChange={setSidepanelOpen}
        isPremium={isPremium}
        isQuotaReached={isQuotaReached}
        currentEmailAccountsCount={currentEmailAccountsCount}
        currentMaxEmailAccounts={currentMaxEmailAccounts}
        absoluteMaxEmailAccounts={absoluteMaxEmailAccounts}
        planIncludedEmailAccounts={planIncludedEmailAccounts}
        pricePerSlot={pricePerSlot}
        currencySymbol={currencySymbol}
        currencyFormat={currencyFormat}
        billingCycle={billingCycle}
        subscriptionLevel={subscriptionLevel}
        creditCardInfo={creditCardInfo}
        nextBillingDate={nextBillingDate}
        billingPeriodStart={billingPeriodStart}
        stripePublicKey={stripePublicKey}
        billingInfo={billingInfo}
        userEmail={userEmail}
        cardholderName={cardholderName}
        hasCreditCard={hasCreditCard}
        filter={sidepanelFilter}
        allowEmailProvisioning={allowEmailProvisioning}
        managedMailboxMonthlyPrice={managedMailboxMonthlyPrice}
      />
    </>
  )
}
